import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";

export default function EditModal({ open, onOpenChange, resource, onSave }) {
  const [form, setForm] = useState({
    title: "",
    subtitle: "",
    insight_paragraph: "",
    keywords: [],
    actionable_steps: [],
  });
  const [kwInput, setKwInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (resource) {
      setForm({
        title: resource.title || "",
        subtitle: resource.subtitle || "",
        insight_paragraph: resource.insight_paragraph || "",
        keywords: [...(resource.keywords || [])],
        actionable_steps: [...(resource.actionable_steps || [])],
      });
    }
  }, [resource]);

  const addKeyword = () => {
    const v = kwInput.trim().toLowerCase();
    if (v && !form.keywords.includes(v)) {
      setForm((f) => ({ ...f, keywords: [...f.keywords, v] }));
    }
    setKwInput("");
  };

  const removeKeyword = (kw) => {
    setForm((f) => ({ ...f, keywords: f.keywords.filter((k) => k !== kw) }));
  };

  const submit = async () => {
    setSaving(true);
    try {
      await onSave(form);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="edit-modal"
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            Modifica scheda
          </DialogTitle>
          <DialogDescription>
            Aggiorna titolo, keyword e contenuti distillati.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-2">
          <div>
            <Label htmlFor="title" className="text-xs font-semibold tracking-wider uppercase text-zinc-500">
              Titolo
            </Label>
            <Input
              id="title"
              data-testid="edit-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1.5"
            />
          </div>

          <div>
            <Label className="text-xs font-semibold tracking-wider uppercase text-zinc-500">
              Sottotitolo
            </Label>
            <Input
              data-testid="edit-subtitle"
              value={form.subtitle}
              onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
              className="mt-1.5"
            />
          </div>

          <div>
            <Label className="text-xs font-semibold tracking-wider uppercase text-zinc-500">
              Panoramica
            </Label>
            <Textarea
              data-testid="edit-insight"
              rows={4}
              value={form.insight_paragraph}
              onChange={(e) =>
                setForm({ ...form, insight_paragraph: e.target.value })
              }
              className="mt-1.5 resize-none"
            />
          </div>

          <div>
            <Label className="text-xs font-semibold tracking-wider uppercase text-zinc-500">
              Keywords
            </Label>
            <div className="flex gap-2 mt-1.5">
              <Input
                data-testid="edit-keyword-input"
                value={kwInput}
                onChange={(e) => setKwInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addKeyword();
                  }
                }}
                placeholder="Aggiungi keyword e premi Invio"
              />
              <Button
                type="button"
                variant="outline"
                onClick={addKeyword}
                data-testid="edit-add-keyword"
              >
                Aggiungi
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {form.keywords.map((kw) => (
                <Badge
                  key={kw}
                  variant="secondary"
                  className="bg-zinc-100 text-zinc-700 gap-1 pr-1"
                >
                  {kw}
                  <button
                    onClick={() => removeKeyword(kw)}
                    className="ml-1 hover:text-red-600"
                    aria-label={`Rimuovi ${kw}`}
                  >
                    <X size={10} />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="edit-cancel"
          >
            Annulla
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="edit-save"
            style={{ backgroundColor: "#002FA7" }}
            className="text-white hover:opacity-90"
          >
            {saving ? "Salvataggio…" : "Salva modifiche"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
